import {
  createBrowserRouter,
  type RouteObject,
} from 'react-router-dom'

import { RequireAuth } from './auth-guard'
import { RouteErrorBoundary } from './error-boundaries'
import { RootLayout } from './root-layout'
import { AppShell } from './shell/app-shell'
import { BootstrapScreen } from './screens/bootstrap-screen'
import { LegalScreen } from './screens/legal-screen'
import { OnboardingScreen } from './screens/onboarding-screen'
import { AdminDashboardScreen } from '@/features/admin/screens/admin-dashboard-screen'
import { AdminRecordListScreen } from '@/features/admin/screens/admin-record-list-screen'
import { AdminActivityReviewScreen } from '@/features/admin/screens/admin-activity-review-screen'
import { ActivityChatScreen } from '@/features/chat/screens/activity-chat-screen'
import { ChatsScreen } from '@/features/chat/screens/chats-screen'
import { MessagesScreen } from '@/features/chat/screens/messages-screen'
import { ActivityDetailScreen } from '@/features/activity-detail/screens/activity-detail-screen'
import { NotificationsScreen } from '@/features/notifications/screens/notifications-screen'
import { CreateActivityScreen } from '@/features/activities/screens/create-activity-screen'
import { ActivityEditScreen } from '@/features/activities/screens/edit-activity-screen'
import { AgendaScreen } from '@/features/discovery/screens/agenda-screen'
import { BookmarksScreen } from '@/features/discovery/screens/bookmarks-screen'
import { DiscoveryScreen } from '@/features/discovery/screens/discovery-screen'
import { HistoryScreen } from '@/features/discovery/screens/history-screen'
import { FriendsScreen } from '@/features/profile-social/screens/friends-screen'
import { ProfileScreen } from '@/features/profile-social/screens/profile-screen'
import { SettingsScreen } from '@/features/profile-social/screens/settings-screen'
import { SettingsAccountScreen } from '@/features/profile-social/screens/settings-account-screen'
import { SettingsAppScreen } from '@/features/profile-social/screens/settings-app-screen'
import { PublicProfileScreen } from '@/features/profile-social/screens/public-profile-screen'
import { GoogleRegistrationScreen } from '@/features/auth/screens/google-registration-screen'
import { LoginScreen } from '@/features/auth/screens/login-screen'
import { RegisterScreen } from '@/features/auth/screens/register-screen'
import { ResetPasswordScreen } from '@/features/auth/screens/reset-password-screen'
import { VerifyEmailScreen } from '@/features/auth/screens/verify-email-screen'
import { VerifyRegistrationScreen } from '@/features/auth/screens/verify-registration-screen'

const handle = (titleKey: string) => ({ titleKey })

/**
 * Route inventory exactly from docs/first-rewrite/UI_PARITY.md §1. Feature
 * workstreams replace placeholder elements in their own feature folders; the
 * paths and auth boundaries stay here.
 */
export const routes: RouteObject[] = [
  {
    element: <RootLayout />,
    errorElement: <RouteErrorBoundary />,
    children: [
      {
        index: true,
        element: <BootstrapScreen />,
      },
      {
        path: 'onboarding',
        element: <OnboardingScreen />,
      },
      {
        path: 'login',
        element: <LoginScreen />,
        handle: handle('screens.login.title'),
      },
      {
        path: 'register',
        element: <RegisterScreen />,
        handle: handle('screens.register.title'),
      },
      {
        path: 'register-google',
        element: <GoogleRegistrationScreen />,
        handle: handle('screens.googleRegistration.title'),
      },
      {
        path: 'verify-registration',
        element: <VerifyRegistrationScreen />,
        handle: handle('screens.verifyRegistration.title'),
      },
      {
        path: 'verify-email',
        element: <VerifyEmailScreen />,
        handle: handle('screens.verifyEmail.title'),
      },
      {
        path: 'reset-password',
        element: <ResetPasswordScreen />,
        handle: handle('screens.resetPassword.title'),
      },
      {
        path: 'terms',
        element: <LegalScreen kind="terms" />,
        handle: handle('screens.terms.title'),
      },
      {
        path: 'privacy',
        element: <LegalScreen kind="privacy" />,
        handle: handle('screens.privacy.title'),
      },
      {
        element: <RequireAuth />,
        children: [
          {
            element: <AppShell />,
            children: [
              {
                path: 'home',
                element: <DiscoveryScreen pageTitleKey="screens.home.title" />,
                handle: handle('screens.home.title'),
              },
              {
                path: 'activities',
                element: (
                  <DiscoveryScreen pageTitleKey="screens.activities.title" />
                ),
                handle: handle('screens.activities.title'),
              },
              {
                path: 'saved',
                element: <AgendaScreen />,
                handle: handle('screens.saved.title'),
              },
              {
                path: 'bookmarks',
                element: <BookmarksScreen />,
                handle: handle('screens.bookmarks.title'),
              },
              {
                path: 'create',
                element: <CreateActivityScreen />,
                handle: handle('screens.create.title'),
              },
              {
                path: 'chats',
                element: <ChatsScreen />,
                handle: handle('screens.chats.title'),
              },
              {
                path: 'profile',
                element: <ProfileScreen />,
                handle: handle('screens.profile.title'),
              },
              {
                path: 'settings',
                element: <SettingsScreen />,
                handle: handle('screens.settings.title'),
              },
              {
                path: 'settings/account',
                element: <SettingsAccountScreen />,
                handle: handle('screens.settings.account'),
              },
              {
                path: 'settings/app',
                element: <SettingsAppScreen />,
                handle: handle('screens.settings.app'),
              },
            ],
          },
          {
            path: 'activity/:id?',
            element: <ActivityDetailScreen />,
            handle: handle('screens.activity.title'),
          },
          {
            path: 'chat/:id?',
            element: <ActivityChatScreen />,
            handle: handle('screens.chat.title'),
          },
          {
            path: 'users/:id',
            element: <PublicProfileScreen />,
            handle: handle('screens.user.title'),
          },
          {
            path: 'messages/:id?',
            element: <MessagesScreen />,
            handle: handle('screens.messages.title'),
          },
          {
            path: 'friends',
            element: <FriendsScreen />,
            handle: handle('screens.friends.title'),
          },
          {
            path: 'notifications',
            element: <NotificationsScreen />,
            handle: handle('screens.notifications.title'),
          },
          {
            path: 'history',
            element: <HistoryScreen />,
            handle: handle('screens.history.title'),
          },
          {
            path: 'admin-dashboard',
            element: <AdminDashboardScreen />,
            handle: handle('screens.adminDashboard.title'),
          },
          {
            path: 'admin-dashboard/users',
            element: <AdminRecordListScreen kind="users" />,
            handle: handle('screens.adminDashboard.title'),
          },
          {
            path: 'admin-dashboard/activities',
            element: <AdminRecordListScreen kind="activities" />,
            handle: handle('screens.adminDashboard.title'),
          },
          {
            path: 'admin-dashboard/activities/:id',
            element: <AdminActivityReviewScreen />,
            handle: handle('screens.adminDashboard.title'),
          },
          {
            path: 'activity-edit/:id?',
            element: <ActivityEditScreen />,
            handle: handle('screens.activityEdit.title'),
          },
        ],
      },
      {
        path: '*',
        element: <BootstrapScreen />,
      },
    ],
  },
]

export function createAppRouter() {
  return createBrowserRouter(routes)
}
